import type { ReactNode } from 'react';
import { Minus, Plus } from '@phosphor-icons/react';
import { cx } from '../cx';
import { PageContainer } from '../PageContainer/PageContainer';
import { Icon } from '../Icon';

export type FooterLink = { label: ReactNode; href: string };
export type FooterGroup = { id: string; title: ReactNode; links: FooterLink[] };

export type FooterProps = {
  /** Logo and the one-line brand statement. */
  brand: ReactNode;
  groups: FooterGroup[];
  /** The company behind the site, with its business ID. */
  legalEntity: ReactNode;
  /** The cookie settings control: required, so it stays reachable on every page. */
  cookieSettings: ReactNode;
  /** The rest of the bottom row: language switch, privacy and terms links. */
  legal?: ReactNode;
  /** Accessible name of the footer navigation. */
  label: string;
  className?: string;
};

const linkClass =
  'rounded-jc-info text-sm text-jc-ink-secondary hover:text-jc-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus';

function Links({ links }: { links: FooterLink[] }) {
  return (
    <ul className="flex flex-col gap-2.5">
      {links.map((l) => (
        <li key={l.href}>
          <a href={l.href} className={linkClass}>
            {l.label}
          </a>
        </li>
      ))}
    </ul>
  );
}

/**
 * The site footer: brand line, link groups (columns from 768px, an accordion below), and the legal
 * row. The accordion is native details, so every link stays in the HTML.
 */
export function Footer({ brand, groups, legalEntity, cookieSettings, legal, label, className }: FooterProps) {
  return (
    <footer className={cx('border-t border-jc-border bg-jc-surface', className)}>
      <PageContainer className="flex flex-col gap-jc-block py-jc-section">
        <div className="flex flex-col gap-8 md:flex-row md:gap-12">
          <div className="flex max-w-xs flex-col gap-3 text-sm text-jc-ink-secondary">{brand}</div>
          <nav aria-label={label} className="flex-1">
            <div className="hidden gap-8 md:grid md:grid-cols-3 lg:grid-cols-4">
              {groups.map((g) => (
                <div key={g.id} className="flex flex-col gap-3">
                  <h2 className="type-eyebrow text-jc-ink-secondary">{g.title}</h2>
                  <Links links={g.links} />
                </div>
              ))}
            </div>
            <div className="divide-y divide-jc-border border-y border-jc-border md:hidden">
              {groups.map((g) => (
                <details key={g.id} className="group">
                  <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between text-sm font-semibold text-jc-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-jc-focus [&::-webkit-details-marker]:hidden">
                    {g.title}
                    <Icon icon={Plus} size="sm" className="group-open:hidden" />
                    <Icon icon={Minus} size="sm" className="hidden group-open:block" />
                  </summary>
                  <div className="pb-4">
                    <Links links={g.links} />
                  </div>
                </details>
              ))}
            </div>
          </nav>
        </div>
        <div className="flex flex-col gap-3 border-t border-jc-border pt-6 text-xs text-jc-ink-secondary md:flex-row md:flex-wrap md:items-center md:justify-between">
          <span>{legalEntity}</span>
          <span className="flex flex-wrap items-center gap-4">
            {legal}
            {cookieSettings}
          </span>
        </div>
      </PageContainer>
    </footer>
  );
}
